// Pin-protocol compliance — SHADOW MODE (pasture-pin plan Phase 5).
//
// The manager dashboard's view of last week's ground-MRV discipline: the
// crew's completion + quality composite and each observer's own rates.
// Deliberately labelled as shadow: these scores are informational and
// linked to nothing until the payment plan's shadow month wires them into
// the weekly consensus gate. The card renders only when a week was
// actually measured — no pin program, no card, no noise.

import { useQuery } from '@tanstack/react-query'
import { ClipboardCheck, Eye } from 'lucide-react'
import { getPinCompliance } from '../services/api'

const pct = (v) => (v == null ? '—' : `${Math.round(v * 100)}%`)

function scoreTone(score) {
  if (score == null) return 'text-gray-400'
  if (score >= 85) return 'text-green-700'
  if (score >= 60) return 'text-amber-700'
  return 'text-red-700'
}

export default function PinComplianceCard({ ranchId }) {
  const { data } = useQuery({
    queryKey: ['pinCompliance', ranchId],
    queryFn: () => getPinCompliance(ranchId),
    enabled: !!ranchId,
    retry: (count, err) => err?.response?.status !== 403 && count < 2,
  })

  if (!data?.measured) return null

  const { crew, people, week_start, week_end, excluded_paddocks } = data

  return (
    <div className="bg-white rounded-lg shadow p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5 text-green-700" />
          <h2 className="text-base font-semibold text-gray-900">Pasture pin compliance</h2>
          <span className="text-xs text-gray-400">
            {new Date(week_start).toLocaleDateString()} – {new Date(week_end).toLocaleDateString()}
          </span>
        </div>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs font-medium"
              title="Informational only — not linked to pay until the payment plan's shadow month.">
          <Eye className="h-3 w-3" /> Shadow
        </span>
      </div>

      <div className="flex items-baseline gap-6 mb-3">
        <div>
          <div className={`text-3xl font-bold tabular-nums ${scoreTone(crew.score)}`}>
            {crew.score != null ? crew.score : '—'}
          </div>
          <div className="text-xs text-gray-500">crew score</div>
        </div>
        <div className="text-sm text-gray-600">
          {crew.done}/{crew.assigned} pins done ({pct(crew.completion_rate)})
          {crew.quality_rate != null && <> · capture quality {pct(crew.quality_rate)}</>}
        </div>
      </div>

      {people.length > 0 && (
        <div className="divide-y divide-gray-100 text-sm">
          {people.map((p) => (
            <div key={p.user_id} className="py-1.5 flex items-center justify-between gap-3">
              <span className="text-gray-800 truncate">{p.name}</span>
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {p.observations} pin{p.observations > 1 ? 's' : ''}
                {' · '}on-time {pct(p.on_time)} · at-pin {pct(p.at_pin)} · full {pct(p.complete)}
              </span>
              <span className={`font-semibold tabular-nums ${scoreTone(p.score)}`}>{p.score}</span>
            </div>
          ))}
        </div>
      )}

      {excluded_paddocks.length > 0 && (
        <p className="text-xs text-gray-400 mt-2">
          {excluded_paddocks.join(', ')} excluded this week — an incursion was
          reported there, and reporting never counts against the crew.
        </p>
      )}
    </div>
  )
}
